<script setup>
import { computed, ref, watch } from 'vue'
import { api } from '@/lib/http.js'
import { formatMoney } from '@/lib/format.js'
import { reportError } from '@/composables/useSave.js'
import UiSpinner from '@/components/ui/UiSpinner.vue'
import BillingLines from '@/components/reports/BillingLines.vue'

/**
 * Invoicing on a contract's or an agreement's page (D-216): its billing schedule — each stage and
 * each service's month, due, overdue, invoiced or planned, with the invoice's number — marked
 * invoiced here as in the "To invoice" report. Its lines go up (`loaded`) for the stages' and the
 * activations' marks.
 */
const props = defineProps({ contractUuid: { type: String, required: true }, agreementUuid: { type: String, default: '' }, currency: { type: String, default: 'PLN' } })
const emit = defineEmits(['loaded'])
const data = ref(null)
const shown = ref('')
const STATES = ['due', 'overdue', 'invoiced', 'planned']

async function load() {
  try {
    data.value = (await api(`/contracts/${props.contractUuid}/billing`, { query: props.agreementUuid ? { agreement: props.agreementUuid } : {} })).data
    // What waits first, when anything does.
    if (!shown.value) shown.value = data.value.totals.due.count + data.value.totals.overdue.count ? 'open' : 'all'
    emit('loaded', data.value.lines)
  } catch (e) {
    reportError(e)
  }
}
watch(() => [props.contractUuid, props.agreementUuid], load, { immediate: true })
defineExpose({ reload: load })

const lines = computed(() => (data.value?.lines || []).filter((l) => shown.value === 'all' || (shown.value === 'open' ? ['due', 'overdue'].includes(l.status) : l.status === shown.value)))
const total = (s) => Object.entries(data.value.totals[s].by_currency || {}).map(([c, v]) => formatMoney(v, c || props.currency)).join(' + ') || formatMoney(0, props.currency)
</script>

<template>
  <section class="card" data-testid="contract-billing">
    <header class="flex flex-wrap items-center gap-2 border-b border-default px-4 py-3">
      <h3 class="flex-1 font-semibold"><i class="fa-solid fa-file-invoice-dollar mr-1 text-faint" aria-hidden="true"></i>{{ $t('billing.section') }}</h3>
      <div v-if="data" class="flex flex-wrap gap-1" role="radiogroup">
        <button v-for="s in ['open', ...STATES, 'all']" :key="s" type="button" role="radio" :aria-checked="shown === s"
                :class="['rounded-full px-2.5 py-0.5 text-xs', shown === s ? 'bg-brand-600 text-white' : 'bg-[var(--surface-3)] text-muted hover:text-[var(--text)]']" :data-testid="`contract-billing-${s}`" @click="shown = s">
          {{ $t(`billing.filter.${s}`) }}<template v-if="STATES.includes(s)"> · {{ data.totals[s].count }}</template>
        </button>
      </div>
    </header>
    <div v-if="!data" class="py-6 text-center"><UiSpinner /></div>
    <template v-else>
      <p v-if="data.totals.overdue.count || data.totals.due.count" class="px-4 pt-3 text-sm">
        <span v-if="data.totals.overdue.count" class="font-medium text-red-600">{{ $t('billing.states.overdue') }}: {{ total('overdue') }}</span>
        <span v-if="data.totals.overdue.count && data.totals.due.count" class="text-faint"> · </span>
        <span v-if="data.totals.due.count" class="font-medium text-amber-700 dark:text-amber-300">{{ $t('billing.states.due') }}: {{ total('due') }}</span>
      </p>
      <BillingLines :lines="lines" :can-invoice="data.can_invoice" :currency="currency" compact @changed="load" />
    </template>
  </section>
</template>
